import { createRootRoute, HeadContent, Link, Outlet, Scripts } from '@tanstack/react-router'
import { GTProvider } from 'gt-react'
import { type ReactNode, StrictMode } from 'react'
import { ThemeProvider, themeInitScript } from '@/components/theme-provider'
import enDictionary from '@/dictionaries/en-GB.json'
import loadDictionary from '@/lib/loadDictionary'
import gtConfig from '../../gt.config.json'
// Side-effect imports: fonts + global styles
// @ts-expect-error — CSS-only import has no TS declaration
import '@fontsource/geist'
// @ts-expect-error — CSS-only import has no TS declaration
import '@fontsource/geist-mono'
import '@/styles/globals.css'

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
      {
        title: 'Cloud ERP — Modular ERP for companies that actually move',
      },
      {
        name: 'description',
        content:
          'Modular, multi-tenant cloud ERP. Pick the modules you need, build your own entities and workflows, and own your data.',
      },
      {
        property: 'og:title',
        content: 'Cloud ERP',
      },
      {
        property: 'og:description',
        content: 'The modular ERP built for companies that actually move.',
      },
      {
        property: 'og:type',
        content: 'website',
      },
    ],
  }),
  shellComponent: RootDocument,
  component: () => <Outlet />,
  notFoundComponent: NotFoundPage,
})

function NotFoundPage() {
  return (
    <section className="flex min-h-[80vh] items-center justify-center px-4">
      <div className="text-center">
        <p className="text-6xl font-bold tracking-tight marketing-gradient-text">404</p>
        <h1 className="mt-4 text-2xl font-semibold">Page not found</h1>
        <p className="mt-2 max-w-md text-muted-foreground">
          The page you're looking for doesn't exist, or it moved.
        </p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center rounded-full border border-border bg-background/60 px-5 py-2 text-sm font-medium backdrop-blur hover:bg-accent hover:text-accent-foreground transition-colors"
        >
          Home
        </Link>
      </div>
    </section>
  )
}

function RootDocument({ children }: { children: ReactNode }) {
  return (
    <html lang="en-GB" suppressHydrationWarning>
      <head>
        <HeadContent />
        {/*
          Theme-init script runs before hydration so the html class is
          correct on first paint. Without this, dark-mode users would
          see a white flash before React picks up localStorage.
        */}
        <script
          // biome-ignore lint/security/noDangerouslySetInnerHtml: trusted inline bootstrap
          dangerouslySetInnerHTML={{ __html: themeInitScript }}
        />
      </head>
      <body className="bg-background text-foreground antialiased font-sans">
        <StrictMode>
          <GTProvider
            loadDictionary={loadDictionary}
            config={gtConfig}
            dictionary={enDictionary}
            runtimeUrl={null}
            cacheUrl={null}
          >
            <ThemeProvider>{children}</ThemeProvider>
          </GTProvider>
        </StrictMode>
        <Scripts />
      </body>
    </html>
  )
}
